import "./Analyst.feed.scss";

/**
 * Lovable poster + circular play over prod Wistia (same pattern as PostDetail / Sale).
 * Embed mounts only after play so domain-auth errors don't replace the poster.
 */
import React from "react";
import PropTypes from "prop-types";
import { AnalystPlayIcon, AnalystVideoIcon } from "./AnalystIcons";

const FAILURE_PHRASES = [
  "media not found",
  "video not found",
  "not authorized",
  "not authorised",
];

const playAnalystWistia = (mediaId) => {
  if (!mediaId || typeof window === "undefined") return;

  const tryPlay = () => {
    const video = window.Wistia?.api?.(mediaId);
    if (!video || typeof video.play !== "function") return false;
    try {
      video.play();
    } catch (_) {
      /* ignore */
    }
    return true;
  };

  if (tryPlay()) return;

  if (window.Wistia?.embeds?.setup) {
    window.Wistia.embeds.setup();
  }

  window.setTimeout(tryPlay, 120);
  window.setTimeout(tryPlay, 500);
};

class AnalystFeaturedVideo extends React.Component {
  constructor(props) {
    super(props);
    this.containerRef = React.createRef();
    this.state = {
      showFallback: !props.mediaId,
      started: false,
    };
  }

  componentDidUpdate(_prevProps, prevState) {
    if (!this.state.started || prevState.started) return;

    this.checkInterval = window.setInterval(this.detectFailure, 600);
    this.failTimeout = window.setTimeout(() => {
      if (this.state.showFallback) return;

      const root = this.containerRef.current;
      if (!root) return;

      if (this.hasFailureText(root)) {
        this.setState({ showFallback: true });
        return;
      }

      const embed = root.querySelector(".wistia_embed");
      const hasPlayer =
        embed &&
        (embed.querySelector("iframe") ||
          embed.querySelector("video") ||
          embed.querySelector("wistia-player"));
      if (!hasPlayer) {
        this.setState({ showFallback: true });
      }
    }, 4000);
  }

  componentWillUnmount() {
    window.clearInterval(this.checkInterval);
    window.clearTimeout(this.failTimeout);
  }

  hasFailureText = (root) => {
    const text = (root.textContent || "").toLowerCase();
    return FAILURE_PHRASES.some((phrase) => text.includes(phrase));
  };

  detectFailure = () => {
    const root = this.containerRef.current;
    if (!root || this.state.showFallback) return;

    if (this.hasFailureText(root)) {
      this.setState({ showFallback: true, started: false });
      window.clearInterval(this.checkInterval);
    }
  };

  handlePlay = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (this.state.showFallback || !this.props.mediaId) return;

    this.setState({ started: true }, () => {
      playAnalystWistia(this.props.mediaId);
    });
  };

  renderPlaceholder() {
    return (
      <div className="analyst-profile-video__placeholder">
        <AnalystVideoIcon />
        <span>Featured video coming soon</span>
      </div>
    );
  }

  render() {
    const { mediaId, posterUrl, name } = this.props;
    const { showFallback, started } = this.state;

    if (!mediaId || showFallback) {
      return this.renderPlaceholder();
    }

    return (
      <div
        className={`analyst-profile-video__inner${
          started ? " is-playing" : ""
        }`}
        ref={this.containerRef}
      >
        {started ? (
          <div
            className={`wistia_embed wistia_async_${mediaId} videoFoam=true playButton=false`}
          />
        ) : null}

        {!started && (
          <button
            type="button"
            className="analyst-profile-video__poster"
            onClick={this.handlePlay}
            aria-label={`Play featured video${name ? ` from ${name}` : ""}`}
          >
            {posterUrl ? (
              <img src={posterUrl} alt="" loading="lazy" />
            ) : (
              <span
                className="analyst-profile-video__poster-fallback"
                aria-hidden="true"
              />
            )}
            <span className="analyst-profile-video__play" aria-hidden="true">
              <span>
                <AnalystPlayIcon />
              </span>
            </span>
          </button>
        )}
      </div>
    );
  }
}

AnalystFeaturedVideo.propTypes = {
  mediaId: PropTypes.string,
  posterUrl: PropTypes.string,
  name: PropTypes.string,
};

export default AnalystFeaturedVideo;
